/**
 * Layout 5: Nature Minimal
 * Scandinavian/Clean aesthetic aligned with noma.to identity
 * - Extremely refined and sober
 * - Generous whitespace
 * - Subtle organic shapes as decorations
 * - Map as calm centerpiece
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { NavigationControl } from "react-map-gl/mapbox";
import type { Map as MapboxMap } from "mapbox-gl";
import {
  type PublishedLandingPage,
  type PlaceFeature,
  type PlaceGeoJSON,
  PLACE_TYPE_META,
  PlaceType,
  getPlaceTrack,
} from "../../lib/api";
import { FIT_BOUNDS_PADDING, FLY_TO_OPTIONS, FLY_TO_ZOOM } from "../../lib/mapConstants";
import { routes } from "../../lib/routes";
import { useShare } from "../../hooks/useShare";
import { NomaMap, AdventureMapPlacesLayer, SelectedPlaceLayer, RouteTrackLayer } from "../../components/map";

interface NatureMinimalLandingProps {
  mapData: PublishedLandingPage;
  places: PlaceGeoJSON;
}

export default function NatureMinimalLanding({ mapData, places }: NatureMinimalLandingProps) {
  const share = useShare();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<PlaceFeature | null>(null);

  const placesByType = useMemo(() => {
    const grouped = new Map<PlaceType, PlaceFeature[]>();
    places.features.forEach((place) => {
      const type = place.properties.type;
      if (!grouped.has(type)) grouped.set(type, []);
      grouped.get(type)!.push(place);
    });
    return grouped;
  }, [places.features]);

  const handleMapLoad = useCallback((map: MapboxMap) => {
    mapRef.current = map;
  }, []);

  const handlePlaceSelect = useCallback(
    (placeId: string) => {
      const place = places.features.find((f) => f.id === placeId);
      if (!place) return;
      setSelectedPlace(place);
      mapRef.current?.flyTo({
        ...FLY_TO_OPTIONS,
        center: place.geometry.coordinates as [number, number],
        zoom: FLY_TO_ZOOM,
      });
      if (mapContainerRef.current) {
        const top = mapContainerRef.current.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top, behavior: "smooth" });
      }
    },
    [places.features],
  );

  const handlePlaceClick = useCallback(
    (feature: PlaceFeature) => {
      handlePlaceSelect(feature.id as string);
    },
    [handlePlaceSelect],
  );

  const handleShare = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (mapData.slug) {
        share({
          url: routes.to.landingPages.bySlug(mapData.slug),
          title: mapData.name,
        });
      }
    },
    [share, mapData.slug, mapData.name],
  );

  // Color palette for blobs and accents - ties everything together
  const blobColors = {
    olive: "#d2ef6c",
    oliveLight: "#e8f4a8",
    sage: "#bdece1",
    sageLight: "#daf5ef",
    cream: "#f0f4e8",
    sand: "#e8e4d6",
  };

  // Multiple organic blob shapes for variety
  const BlobShape1 = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
    <svg viewBox="0 0 200 200" className={className} style={style}>
      <path
        fill="currentColor"
        d="M47.5,-57.2C59.9,-46.8,67.5,-30.7,71.3,-13.3C75.2,4.1,75.3,22.8,67.4,37.1C59.5,51.4,43.6,61.3,26.9,67.1C10.2,72.9,-7.3,74.6,-23.8,70.1C-40.3,65.6,-55.8,54.9,-65.6,40.2C-75.4,25.5,-79.5,6.8,-76.2,-10.3C-72.9,-27.4,-62.2,-42.9,-48.1,-53.1C-34,-63.3,-17,-68.2,0.3,-68.6C17.6,-69,35.1,-67.6,47.5,-57.2Z"
        transform="translate(100 100)"
      />
    </svg>
  );

  const BlobShape2 = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
    <svg viewBox="0 0 200 200" className={className} style={style}>
      <path
        fill="currentColor"
        d="M39.9,-51.3C52.5,-41.8,64.1,-30.6,69.5,-16.5C74.9,-2.4,74.1,14.6,67.1,28.8C60.1,43,46.9,54.4,32.1,61.2C17.3,68,0.9,70.3,-16.8,68.3C-34.5,66.3,-53.5,60.1,-63.5,47.4C-73.5,34.7,-74.5,15.5,-71.8,-2.2C-69.1,-19.9,-62.7,-36.1,-51.5,-45.9C-40.3,-55.7,-24.3,-59.1,-9.1,-58.5C6.1,-57.9,27.3,-60.8,39.9,-51.3Z"
        transform="translate(100 100)"
      />
    </svg>
  );

  const BlobShape3 = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
    <svg viewBox="0 0 200 200" className={className} style={style}>
      <path
        fill="currentColor"
        d="M44.3,-52.1C57.5,-42.8,68.1,-28.8,71.7,-13C75.3,2.8,71.9,20.4,63.1,34.7C54.3,49,40.1,60,24.2,65.6C8.3,71.2,-9.3,71.4,-25.2,66C-41.1,60.6,-55.3,49.6,-63.8,35.1C-72.3,20.6,-75.1,2.6,-71.5,-13.5C-67.9,-29.6,-57.9,-43.8,-44.7,-53.1C-31.5,-62.4,-15.8,-66.8,0.1,-66.9C16,-67,31.1,-61.4,44.3,-52.1Z"
        transform="translate(100 100)"
      />
    </svg>
  );

  const BlobShape4 = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
    <svg viewBox="0 0 200 200" className={className} style={style}>
      <path
        fill="currentColor"
        d="M54.2,-59.5C69.3,-48.5,79.8,-30.1,81.5,-11.1C83.2,7.9,76.1,27.5,63.8,42.1C51.5,56.7,34,66.3,15.3,71.5C-3.4,76.7,-23.3,77.5,-40.1,70.1C-56.9,62.7,-70.6,47.1,-76.5,29.1C-82.4,11.1,-80.5,-9.3,-72.2,-26.3C-63.9,-43.3,-49.2,-56.9,-33.5,-67.7C-17.8,-78.5,-1.1,-86.5,14.2,-83.4C29.5,-80.3,39.1,-70.5,54.2,-59.5Z"
        transform="translate(100 100)"
      />
    </svg>
  );

  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{
        fontFamily: "'DM Sans', 'Inter', sans-serif",
        background: "#fafbf7",
      }}
    >
      {/* Header - minimal and airy */}
      <header className="px-8 py-6 flex justify-between items-center max-w-7xl mx-auto">
        <a
          href={routes.to.home()}
          className="text-sm tracking-wide opacity-50 hover:opacity-100 transition-opacity"
          style={{ color: "#536600" }}
        >
          noma.to
        </a>
        <button
          onClick={handleShare}
          className="text-sm tracking-wide opacity-50 hover:opacity-100 transition-opacity flex items-center gap-2"
          style={{ color: "#536600" }}
        >
          <span>Share</span>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M4 10l6-6M10 4v6M10 4H4" />
          </svg>
        </button>
      </header>

      {/* Hero - centered, calm */}
      <section className="relative px-8 py-16 max-w-7xl mx-auto">
        {/* Decorative blobs - varied shapes, colors, opacities */}
        <BlobShape1
          className="absolute -top-20 right-0 w-80 h-80 opacity-25 translate-x-1/3"
          style={{ color: blobColors.olive }}
        />
        <BlobShape2 className="absolute top-40 right-0 w-48 h-48 opacity-15" style={{ color: blobColors.sage }} />
        <BlobShape3
          className="absolute -bottom-32 -left-40 w-80 h-80 opacity-30"
          style={{ color: blobColors.sageLight }}
        />
        <BlobShape4
          className="absolute bottom-20 -left-20 w-40 h-40 opacity-20"
          style={{ color: blobColors.oliveLight }}
        />

        <div className="relative z-10 text-center max-w-2xl mx-auto mb-16">
          <h1
            className="text-5xl md:text-6xl font-light leading-tight mb-6"
            style={{
              color: "#1b1c17",
              fontFamily: "'Instrument Serif', 'Georgia', serif",
            }}
          >
            {mapData.name}
          </h1>
          {mapData.description && (
            <p className="text-lg leading-relaxed" style={{ color: "#5c6146" }}>
              {mapData.description}
            </p>
          )}
        </div>

        {/* Map - rounded, soft shadow */}
        <div ref={mapContainerRef} className="relative max-w-5xl mx-auto">
          <div
            className="h-[500px] rounded-3xl overflow-hidden [&_.mapboxgl-ctrl-bottom-left]:!left-4 [&_.mapboxgl-ctrl-bottom-left]:!bottom-4 [&_.mapboxgl-ctrl-bottom-right]:!right-4 [&_.mapboxgl-ctrl-bottom-right]:!bottom-4"
            style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.08)" }}
          >
            <NomaMap bounds={places} padding={FIT_BOUNDS_PADDING.desktop} onLoad={handleMapLoad}>
              <NavigationControl position="bottom-right" />
              <AdventureMapPlacesLayer places={places} onClick={handlePlaceClick} />
              <RouteTrackLayer track={selectedPlace ? getPlaceTrack(selectedPlace.properties) : undefined} />
              <SelectedPlaceLayer place={selectedPlace} />
            </NomaMap>
          </div>

          {/* Selected place panel */}
          {selectedPlace &&
            (() => {
              // Get accent color based on place type
              const typeIndex = Array.from(placesByType.keys()).indexOf(selectedPlace.properties.type);
              const sectionColors = [
                {
                  bg: blobColors.oliveLight,
                  border: blobColors.olive,
                  text: "#536600",
                },
                {
                  bg: blobColors.sageLight,
                  border: blobColors.sage,
                  text: "#3d6b5f",
                },
                { bg: blobColors.cream, border: "#c5d4a0", text: "#536600" },
              ];
              const accent = sectionColors[typeIndex % sectionColors.length];

              return (
                <div className="absolute top-6 left-6 max-w-sm animate-in fade-in slide-in-from-left-2 duration-300">
                  <div
                    className="p-6 rounded-2xl backdrop-blur-md"
                    style={{
                      background: "rgba(250,251,247,0.95)",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                    }}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <span
                        className="text-xs tracking-wide px-3 py-1 rounded-full"
                        style={{ background: accent.bg, color: accent.text }}
                      >
                        {PLACE_TYPE_META[selectedPlace.properties.type].label}
                      </span>
                      <button
                        onClick={() => setSelectedPlace(null)}
                        className="w-6 h-6 rounded-full flex items-center justify-center transition-colors"
                        style={{ color: "#5c6146" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = accent.bg)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" stroke="currentColor" strokeWidth="1.5">
                          <path d="M2 2l8 8M10 2L2 10" />
                        </svg>
                      </button>
                    </div>
                    <h3
                      className="text-xl font-medium mb-2"
                      style={{
                        color: "#1b1c17",
                        fontFamily: "'Instrument Serif', 'Georgia', serif",
                      }}
                    >
                      {selectedPlace.properties.name}
                    </h3>
                    {selectedPlace.properties.description && (
                      <p className="text-sm leading-relaxed" style={{ color: "#5c6146" }}>
                        {selectedPlace.properties.description}
                      </p>
                    )}
                  </div>
                </div>
              );
            })()}
        </div>
      </section>

      {/* Places sections - clean and minimal */}
      <main className="relative max-w-4xl mx-auto px-8 py-20">
        {/* More decorative blobs in content area */}
        <BlobShape2 className="absolute top-10 -right-32 w-56 h-56 opacity-15" style={{ color: blobColors.olive }} />
        <BlobShape3 className="absolute top-1/3 -left-28 w-44 h-44 opacity-20" style={{ color: blobColors.sand }} />
        <BlobShape1 className="absolute bottom-1/4 -right-24 w-36 h-36 opacity-15" style={{ color: blobColors.sage }} />

        {places.features.length === 0 ? (
          <div className="text-center py-16" style={{ color: "#77786a" }}>
            <p className="text-lg">Places will appear here</p>
          </div>
        ) : (
          <div className="relative z-10 space-y-20">
            {Array.from(placesByType.entries()).map(([type, typePlaces], idx) => {
              // Alternate accent colors per section
              const sectionColors = [
                {
                  bg: blobColors.oliveLight,
                  border: blobColors.olive,
                  text: "#536600",
                },
                {
                  bg: blobColors.sageLight,
                  border: blobColors.sage,
                  text: "#3d6b5f",
                },
                {
                  bg: blobColors.cream,
                  border: "#c5d4a0",
                  text: "#536600",
                },
              ];
              const accent = sectionColors[idx % sectionColors.length];

              return (
                <section key={type}>
                  {/* Section header - understated */}
                  <div className="flex items-center gap-4 mb-10">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ background: accent.bg }}
                    >
                      <span className="text-xs font-medium" style={{ color: accent.text }}>
                        {typePlaces.length}
                      </span>
                    </div>
                    <h2
                      className="text-2xl font-light"
                      style={{
                        color: "#1b1c17",
                        fontFamily: "'Instrument Serif', 'Georgia', serif",
                      }}
                    >
                      {PLACE_TYPE_META[type].labelPlural}
                    </h2>
                  </div>

                  {/* Places list - clean cards */}
                  <div className="space-y-3">
                    {typePlaces.map((place) => {
                      const isSelected = selectedPlace?.id === place.id;
                      return (
                        <button
                          key={place.id}
                          onClick={() => handlePlaceSelect(place.id as string)}
                          className="w-full text-left p-6 rounded-2xl transition-all duration-200 group hover:bg-[#f8faf4]"
                          style={{
                            background: isSelected ? accent.bg : "transparent",
                            border: `1px solid ${isSelected ? accent.border : "#e3e4d3"}`,
                          }}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3
                                className="text-lg font-medium mb-1 transition-colors"
                                style={{
                                  color: isSelected ? accent.text : "#1b1c17",
                                }}
                              >
                                {place.properties.name}
                              </h3>
                              {place.properties.description && (
                                <p className="text-sm line-clamp-2 leading-relaxed" style={{ color: "#5c6146" }}>
                                  {place.properties.description}
                                </p>
                              )}
                            </div>
                            <div className="ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                              <div
                                className="w-10 h-10 rounded-full flex items-center justify-center"
                                style={{ background: accent.bg }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  stroke={accent.text}
                                  strokeWidth="1.5"
                                >
                                  <path d="M4 8h8M9 5l3 3-3 3" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </main>

      {/* Footer - minimal */}
      <footer className="relative py-20 px-8 overflow-hidden">
        {/* Decorative blobs */}
        <BlobShape4
          className="absolute -bottom-16 -right-16 w-72 h-72 opacity-20"
          style={{ color: blobColors.olive }}
        />
        <BlobShape2
          className="absolute -bottom-12 -left-20 w-48 h-48 opacity-15"
          style={{ color: blobColors.sageLight }}
        />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <p className="text-sm mb-6" style={{ color: "#77786a" }}>
            Curated with care by {mapData.curatedBy?.displayName ?? "your local adventurers"}
          </p>
          <a
            href={routes.to.home()}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-colors hover:bg-[#e8eed6]"
            style={{ border: "1px solid #c7c8b8", color: "#536600" }}
          >
            <span className="text-sm font-medium">Create your own map</span>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 10l6-6M10 4v6M10 4H4" />
            </svg>
          </a>
        </div>
      </footer>
    </div>
  );
}
